import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { displayCurrencyPage } from '../../client/Redux/actions/junoAction';
import { getShortName, setKycStatus, useJunoCommonFunctions } from '../../client/Juno/helpers';
import { setToken, setClientData } from '../../client/Redux/actions';
import { Avatar, Dialog, DialogContent } from '@mui/material';
import { apiService } from '../../client/common/apiCallService';
import East from '@mui/icons-material/East';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SumsubVerification from '../../client/Juno/client/components/modals/SumsubVerification';
import { setShowVerificationModal } from '../../client/Redux/actions/commonAction';
import VerificationIssue from '../../client/Juno/client/components/modals/VerificationIssue';
import { KYCSTATUSES } from '../../client/Juno/client/constant';
import JunoClientActionModal from '../../client/Juno/client/components/table/JunoClientActionModal';
import JncButton from '../../client/Juno/client/components/global/JncButton';
import JunoVerifyBusinessModal from '../../client/Juno/JunoMoney/components/register/JunoVerifyBusinessModal';
import { clearStorageData } from '../../client/common/getIP';
import JncCryptoTicker from '../components/JncCryptoTicker';
import ToggleTheme from './common/ToggleTheme';

const Header = props => {
    const [mode, setMode] = useState('fiat');
    const [logoOptions, setLogoOptions] = useState([]);
    const clientData = useSelector(state => state.juno.clientData);
    const apiUrl = useSelector(state => state.config.api_url);
    const { name, accountNumber } = clientData ? clientData : '-';
    const headerLogo = sessionStorage.getItem('logo');
    const [selectedHeaderLogo, setSelectedHeaderLogo] = useState('');
    // const cryptoHeaderLogo = sessionStorage.getItem('cryptoLogo')
    const intervalRef = useRef();
    const location = useLocation();
    const storedSettings = localStorage.getItem('tabSettings');
    const redirectURL = sessionStorage.getItem('redirectURL');
    const navigate = useNavigate();
    const { setSelectedLogo } = props;
    const dispatch = useDispatch();
    const kycStatus = localStorage.getItem('kycStatus');
    const [isSumsubEnabled, setIsSumsubEnabled] = useState(false);
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const { useWindowWidth, getGlobalSettings, logout } = useJunoCommonFunctions();
    const windowWidth = useWindowWidth();

    const [showFinalVerificationModal, setShowFinalVerificationModal] = useState(false);

    useEffect(() => {
        handleLogoOptions();
    }, [headerLogo, storedSettings, selectedHeaderLogo]);
    const showVerificationModal = useSelector(state => state.common.showVerificationModal);

    const getTabsByMode = mode => {
        if (mode === 'crypto') {
            return [
                { name: 'Overview', path: '/client' },
                { name: 'Transactions', path: '/client/transactions' },
                { name: 'Addresses', path: '/client/address' },
                { name: 'Statements', path: '/client/statements' },
            ];
        } else {
            return [
                { name: 'Overview', path: '/client' },
                { name: 'Balances', path: '/client/balances' },
                { name: 'Bank Accounts', path: '/client/accounts' },
                { name: 'Transactions', path: '/client/transactions' },
                { name: 'Statements', path: '/client/statements' },
            ];
        }
    };

    // Memoize the tabs to prevent recalculation on every render
    const tabs = useMemo(() => getTabsByMode(mode), [mode]);

    const handleLogoOptions = () => {
        const logos = [
            {
                src: headerLogo,
                alt: 'fiat',
            },
            {
                src: headerLogo,
                alt: 'crypto',
            },
        ];
        const parsedSettings = storedSettings ? JSON.parse(storedSettings) : {};
        const filteredLogos = logos.filter(logo => {
            if (parsedSettings && parsedSettings[`${logo.alt}Tab`] === 'Enable') {
                return true;
            }
            return false;
        });
        setLogoOptions(filteredLogos);
        const activeCurrencyPage =
            localStorage.getItem('activeCurrencyPage') || filteredLogos[0]?.alt;
        const activeLogo = filteredLogos.find(e => e.alt === activeCurrencyPage);
        if (!selectedHeaderLogo || selectedHeaderLogo !== activeLogo?.src) {
            setMode(activeLogo?.alt);
            setSelectedHeaderLogo(activeLogo?.src);
        }
        setSelectedLogo(activeLogo ? [activeLogo] : [filteredLogos[0]]);
    };

    const handleLogo = useCallback(
        val => {
            localStorage.setItem('activeCurrencyPage', val[0]?.alt);
            dispatch(displayCurrencyPage(val[0]?.alt));
            navigate(location.pathname);
        },
        [dispatch, navigate, location.pathname],
    );

    function setFavicon() {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        const api_url = localStorage.getItem('api_url');
        link.href = `${api_url}/images/junoclient/junomoney-logo-icon.svg`;
    }

    function checkForInactivity() {
        const expireTime = localStorage.getItem('expireTime');
        if (expireTime < Date.now()) {
            console.log('User has been logged out due to inactivity.');
            logout();
            clearInterval(intervalRef);
        } else {
            console.log('user is active');
        }
    }
    function updateExpireTime() {
        const expireTime = Date.now() + 15 * 60 * 1000;
        localStorage.setItem('expireTime', expireTime);
    }
    const getSumsubSettings = async () => {
        getGlobalSettings('sumsub', data => {
            const value = data[0]?.value || false;
            setIsSumsubEnabled(value);
        });
    };

    useEffect(() => {
        setFavicon();
        checkForInactivity();
        getSumsubSettings();
        let interval = setInterval(() => {
            checkForInactivity();
        }, 20000);
        intervalRef.current = interval;
        window.addEventListener('click', updateExpireTime);
        window.addEventListener('keypress', updateExpireTime);
        window.addEventListener('scroll', updateExpireTime);
        window.addEventListener('mousemove', updateExpireTime);

        return () => {
            window.removeEventListener('click', updateExpireTime);
            window.removeEventListener('keypress', updateExpireTime);
            window.removeEventListener('scroll', updateExpireTime);
            window.removeEventListener('mousemove', updateExpireTime);
            clearInterval(interval);
        };
    }, []);

    const getSumsubKycStatus = async email => {
        const endpoint = `${
            window.location.hostname === 'localhost' ? apiUrl : ''
        }/restapi/kyc-getKycStatus`;
        apiService(
            endpoint,
            { clientEmail: email },
            async data => {
                if (data) {
                    if (data.reviewStatus === 'completed') {
                        if (data.reviewAnswer === 'GREEN') {
                            setKycStatus(KYCSTATUSES.completed);
                        } else {
                            if (kycStatus === KYCSTATUSES.retry) {
                                setKycStatus(KYCSTATUSES.retry);
                            } else {
                                setKycStatus(KYCSTATUSES.failed);
                            }
                        }
                    } else if (data.reviewStatus === 'init') {
                        setKycStatus(KYCSTATUSES['not-started']);
                    } else {
                        setKycStatus(KYCSTATUSES.pending);
                    }
                } else {
                    setKycStatus(KYCSTATUSES['not-started']);
                }
            },
            error => {
                console.error(error);
            },
        );
    };

    const kycMessages = {
        'not-started': {
            text: 'To execute any transactions, please validate your identity.',
            textColor: 'text-[#B54708]',
            bgColor: 'bg-[#FEF0C7]',
            iconVisible: true,
        },
        pending: {
            text: 'Your identity is under verification. This may take up till 1 business day',
            textColor: 'text-[#004EEB]',
            bgColor: 'bg-[#D1E9FF]',
            iconVisible: false,
        },
        retry: {
            text: 'There was an identity verification error. Press here to fix',
            textColor: 'text-[#C11574]',
            bgColor: 'bg-[#FBE8FF]',
            iconVisible: true,
        },
        failed: {
            text: 'Account Verification issue',
            textColor: 'text-[#C11574]',
            bgColor: 'bg-[#FBE8FF]',
            iconVisible: true,
        },
    };

    const handleModalClick = status => {
        const modalStatus = status === 'retry' ? 'start' : status;
        dispatch(setShowVerificationModal(modalStatus));
    };

    // Use useCallback for event handlers
    const handleTabClick = useCallback(
        path => {
            navigate(path);
        },
        [navigate],
    );

    useEffect(() => {
        (async () => {
            localStorage.setItem('isSumsubEnabled', isSumsubEnabled);
            if (clientData) {
                const {
                    accountType,
                    userRegistrationState: {
                        accountPurpose,
                        transactionActivity,
                        uploadDocuments,
                        finalVerification,
                    } = {},
                } = clientData;

                const isBusiness = accountType === 'Business' && uploadDocuments;
                const isPersonal =
                    accountType === 'Personal' && kycStatus === KYCSTATUSES.completed;
                const isEligibleForFinalVerification =
                    accountPurpose && transactionActivity && !finalVerification;
                if (accountType === 'Personal' && accountPurpose && transactionActivity)
                    dispatch(setShowVerificationModal(kycStatus));

                if ((isBusiness || isPersonal) && isEligibleForFinalVerification) {
                    setShowFinalVerificationModal(true);
                } else setShowFinalVerificationModal(false);

                if (clientData?.accountType === 'Personal') {
                    if (
                        clientData?.onboardingStatus === 'Approved' ||
                        clientData?.kyc?.status === KYCSTATUSES.completed
                    ) {
                        setKycStatus(KYCSTATUSES.completed);
                    } else if (clientData?.kyc?.status === KYCSTATUSES.failed) {
                        setKycStatus(KYCSTATUSES.failed);
                    } else {
                        if (kycStatus && kycStatus !== KYCSTATUSES.completed && isSumsubEnabled) {
                            getSumsubKycStatus(localStorage.getItem('user_name'));
                        }
                    }
                }
            }
        })();
    }, [clientData, kycStatus, isSumsubEnabled]);

    return (
        <>
            {windowWidth > 1100 && mode === 'crypto' ? <div>{<JncCryptoTicker />}</div> : null}
            <header className="w-full shadow-none lg:shadow-sm md:bg-white lg:bg-[#26272B] dark:bg-[#26272B] text-white transition-colors duration-300 z-10 lg:static relative border border-[#0000001F] dark:!border-[#FFFFFF1F] lg:rounded-lg border-t-0 lg:!rounded-t-none">
                <div className="w-full px-4 py-3 flex items-center justify-between">
                    {/* Left Section */}
                    <div className="flex items-center space-x-6">
                        <div className="text-sm font-semibold tracking-wide ">
                            <img
                                src={
                                    selectedHeaderLogo || `${imageBaseUrl}/junoclient/juno-icon.svg`
                                }
                                alt="Juno"
                                data-e2e="juno-img"
                                className={`w-[50.254329681396484px] h-[32px] ${
                                    selectedHeaderLogo
                                        ? ''
                                        : 'object-contain xxs:brightness-0 xxs:dark:invert lg:invert'
                                }`}
                            />
                        </div>
                        {/* Mode Switch - Desktop only */}
                        {logoOptions?.length > 1 && (
                            <div className="hidden lg:flex w-[164px] bg-[#3F3F46]  rounded-lg py-[0.5px] overflow-hidden text-[12px] font-semibold leading-none tracking-[0.01em] uppercase font-inter">
                                <button
                                    onClick={() => {
                                        handleLogo([logoOptions[0]]);
                                        setMode('fiat');
                                        navigate('/client');
                                    }}
                                    className={`w-1/2 h-[32px] flex items-center justify-center transition-all duration-200 rounded-lg
                                      ${
                                          mode === 'fiat'
                                              ? 'bg-white text-black'
                                              : 'bg-[#3F3F46] text-[#A0A0A0] hover:bg-[#ffffff0a]'
                                      }`}
                                    style={{ fontFamily: 'Inter, sans-serif' }}
                                    data-e2e="fiat-btn">
                                    FIAT
                                </button>
                                <button
                                    onClick={() => {
                                        handleLogo([logoOptions[1]]);
                                        setMode('crypto');
                                        navigate('/client');
                                    }}
                                    className={`w-1/2 h-[32px] flex items-center justify-center transition-all duration-200 rounded-lg
                                      ${
                                          mode === 'crypto'
                                              ? 'bg-white text-black'
                                              : 'bg-[#3F3F46] text-[#A0A0A0] hover:bg-[#ffffff0a]'
                                      }`}
                                    style={{ fontFamily: 'Inter, sans-serif' }}
                                    data-e2e="crypto-btn">
                                    CRYPTO
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Center Nav - Desktop only */}
                    <div className="hidden lg:flex items-center space-x-6">
                        <nav className="flex gap-[2px]">
                            {tabs.map(tab => {
                                const isActive = location.pathname === tab.path;

                                return (
                                    <button
                                        key={tab.name}
                                        data-e2e={`${tab.name}-upper-tab`}
                                        onClick={() => handleTabClick(tab.path)}
                                        className={`px-[20px] py-0 h-[36px] flex items-center justify-center rounded transition-colors duration-200 font-medium text-[12px] leading-[100%] tracking-[0.01em] text-center uppercase uppercase whitespace-nowrap border border-[1px] !border-[#ffffff0f]  hover:!border-[#FFFFFF52] ${
                                            isActive
                                                ? 'bg-[#FCFCFC] text-[#18181B]'
                                                : 'text-white border-b border-white/5 hover:text-white hover:border-[#FFFFFF52]'
                                        }`}
                                        style={{
                                            fontFamily: 'Inter, sans-serif',
                                        }}>
                                        {tab.name}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>

                    {/* Mobile & Tablet: Mode switch */}
                    {logoOptions.length > 1 && (
                        <div className="lg:hidden flex w-[164px] h-[32px] dark:bg-[#2B2B2B] bg-[#E4E4E7] rounded-lg p-[2px] text-[12px] font-semibold leading-none tracking-[0.01em] uppercase font-inter">
                            <button
                                onClick={() => {
                                    handleLogo([logoOptions[0]]);
                                    setMode('fiat');
                                }}
                                className={`w-1/2 h-full flex items-center justify-center rounded-[4px] transition-all duration-200 font-medium
                                 ${mode === 'fiat' ? 'bg-white text-black' : 'text-[#A0A0A0]'}`}
                                style={{ fontFamily: 'Inter, sans-serif' }}>
                                FIAT
                            </button>
                            <button
                                onClick={() => {
                                    handleLogo([logoOptions[1]]);
                                    setMode('crypto');
                                }}
                                className={`w-1/2 h-full flex items-center justify-center rounded-[4px] transition-all duration-200 font-medium
                             ${mode === 'crypto' ? 'bg-white text-black' : 'text-[#A0A0A0]'}`}
                                style={{ fontFamily: 'Inter, sans-serif' }}>
                                CRYPTO
                            </button>
                        </div>
                    )}

                    {/* Right Section - Desktop only */}
                    <div className="hidden lg:flex items-center space-x-2">
                        <div className="flex items-center h-8 font-inter font-medium text-[12px] leading-[100%] hover:bg-[#3F3F46] rounded-md px-2 !pr-0 cursor-pointer">
                            <div className="mr-2 text-right">
                                <div
                                    className="text-xs font-Inter font-semibold uppercase"
                                    data-e2e="account-name">
                                    {name}
                                </div>
                                <div
                                    className="text-[#A0A0AB] text-[11px] font-Inter font-semibold no-wrap uppercase"
                                    data-e2e="account-number">
                                    Account NR: {accountNumber}
                                </div>
                            </div>
                            <Link
                                to="/client/profile"
                                data-e2e={getShortName(name)}
                                className="w-8 h-8 flex items-center justify-center rounded-md bg-white text-black text-xs font-semibold uppercase hover:!bg-[#E4E4E7]">
                                <Avatar
                                    imgProps={{
                                        style: {
                                            objectFit: 'fill',
                                        },
                                    }}
                                    src={clientData?.profileImage?.url}
                                    variant="square"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '0.375rem',
                                    }}>
                                    <div className="w-8 h-8 flex items-center justify-center rounded-md bg-white text-black text-xs font-semibold uppercase hover:!bg-[#E4E4E7]">
                                        {getShortName(clientData?.name)}
                                    </div>
                                </Avatar>
                            </Link>
                        </div>
                        <ToggleTheme />
                    </div>

                    {/* Right Section - Mobile & Tablet only */}
                    <div className="lg:hidden flex items-center space-x-2">
                        <Link
                            to="/client/profile"
                            className="w-50px h-26px flex items-center justify-center rounded-md">
                            <Avatar
                                imgProps={{
                                    style: {
                                        objectFit: 'fill',
                                    },
                                }}
                                src={
                                    clientData?.profileImage?.url ||
                                    `${imageBaseUrl}/junoclient/profile-icon.svg`
                                }
                                variant="square"
                                alt="Profile"
                                data-e2e="profile-icon"
                                sx={{
                                    width: '26px',
                                    height: '26px',
                                    borderRadius: '0.375rem',
                                    outline: '1px solid',
                                    outlineColor: 'var(--color-text)',
                                }}
                                className={
                                    !clientData?.profileImage?.url ? 'invert dark:invert-0' : ''
                                }
                            />
                        </Link>
                    </div>
                </div>

                {clientData?.onboardingStatus !== 'Approved' && (
                    <div>
                        {isSumsubEnabled &&
                            clientData?.accountType === 'Personal' &&
                            kycStatus &&
                            clientData?.userRegistrationState?.accountPurpose &&
                            clientData?.userRegistrationState?.transactionActivity &&
                            kycMessages[kycStatus] && (
                                <div
                                    style={{}}
                                    onClick={() => handleModalClick(kycStatus)}
                                    className={`cursor-pointer text-xs lg:text-sm font-semibold ${kycMessages[kycStatus].textColor} ${kycMessages[kycStatus].bgColor}  flex justify-center gap-2.5 h-[30px] lg:h-11 items-center`}>
                                    {kycMessages[kycStatus].text}
                                    {kycMessages[kycStatus].iconVisible && (
                                        <East className="!hidden lg:!block" />
                                    )}
                                </div>
                            )}
                        {showFinalVerificationModal ? (
                            <div
                                style={{}}
                                className={`cursor-pointer  text-xs lg:text-sm font-semibold text-[#364152] bg-[#E3E8EF] flex justify-center gap-2.5 h-[30px] lg:h-11 items-center`}>
                                <AccessTimeIcon className="!hidden lg:!block" fontSize="small" />{' '}
                                Your{' '}
                                {clientData?.accountType === 'Business' ? 'Business' : 'Personal'}{' '}
                                details are under verification. This might take 24h
                            </div>
                        ) : null}
                    </div>
                )}
            </header>

            <JunoVerifyBusinessModal
                open={showVerificationModal === 'verificationPending'}
                onClose={() => dispatch(setShowVerificationModal(''))}
            />

            {/* Verification Error Modal */}
            <VerificationIssue
                open={showVerificationModal === KYCSTATUSES.failed}
                onClose={() => dispatch(setShowVerificationModal(''))}
            />

            {/* Under Verification Modal */}
            <JunoClientActionModal
                dialogClassName="jn-acc-verification-message-sent"
                onClose={() => dispatch(setShowVerificationModal(''))}
                states={{
                    start: true,
                }}
                open={showVerificationModal === KYCSTATUSES.pending}
                dialogContent={
                    <div className="flex justify-center items-center flex-col validationModal">
                        <div className="rounded-full bg-[#FAFAFA] p-3">
                            <div className=" bg-[#F4F4F5] rounded-full p-3 ">
                                <div className="rounded-full flex items-center justify-center">
                                    <AccessTimeIcon className="!w-10 !h-10 text-[#51525C] m-0" />
                                </div>
                            </div>
                        </div>
                        <p className="validationModal font-semibold mt-8">
                            Your identity is under verification.
                        </p>
                        <p className="validationModal-subtitle">
                            In order to execute any transaction we require you to validate your
                            identity. Our trusted partner will handle the next step.
                        </p>
                    </div>
                }
                dialogActions={
                    <div className="jncModal__resp w-full">
                        <JncButton
                            text={<p className="!text-white font-medium">I understand</p>}
                            onClickCall={() => dispatch(setShowVerificationModal(''))}
                            className={'w-full !bg-[#18181B]'}
                        />
                    </div>
                }
            />
            {/* First Modal to begin verification */}
            <Dialog
                fullScreen={windowWidth < 768}
                className="jncModal "
                onClose={() => dispatch(setShowVerificationModal(''))}
                open={showVerificationModal === KYCSTATUSES['not-started']}>
                <DialogContent dividers>
                    <div className="rounded-[12px] h-full border border-[#F2F4F7]  shadow-[0_4px_30px_rgba(0,0,0,0.16)] bg-gradient-to-b from-[#292929] to-[#000000]">
                        <div className="flex flex-col items-center justify-center w-full h-full md:h-[557px] gap-9 md:gap-12">
                            <img
                                className="h-7"
                                src={`${imageBaseUrl}/landingpage/light-logo.svg`}
                            />
                            <img
                                className="h-28 object-none"
                                src={`${imageBaseUrl}/verification/group-flags.svg`}
                            />
                            <div className="font-inter text-white text-center px-4 sm:!px-0 sm:w-[498px]">
                                <p
                                    className="text-xl font-semibold"
                                    data-e2e="Welcome to JunoMoney!"
                                    data-e2e-mes="wel-come-message">
                                    Welcome to JunoMoney!
                                </p>
                                <p className="mt-2 text-base">
                                    Congratulations on opening your JunoMoney account! To enjoy all
                                    the features make sure to verify your account.
                                </p>
                            </div>
                            <div className="font-semibold flex flex-col !gap-5 items-center text-base">
                                <div
                                    onClick={() => dispatch(setShowVerificationModal('start'))}
                                    className={`jncAuth__Button w-full rounded-full px-8 py-3 text-[#18181B] font-semibold cursor-pointer full-width bg-white`}>
                                    Begin Verification
                                </div>
                                <p
                                    onClick={() => dispatch(setShowVerificationModal(''))}
                                    className=" text-white cursor-pointer">
                                    Verify Later
                                </p>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {['start', KYCSTATUSES.retry].includes(showVerificationModal) && (
                <SumsubVerification
                    start={['start', KYCSTATUSES.retry].includes(showVerificationModal)}
                    onClose={() => dispatch(setShowVerificationModal(''))}
                    // newID={kycStatus === KYCSTATUSES.retry}
                />
            )}
        </>
    );
};

// Export with React.memo to prevent unnecessary re-renders
export default React.memo(Header);
